"use client";

import type React from "react";

import { Download } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import useApiHook from "../../../utils/useApi";
import DataTable, { type TableColumn } from "react-data-table-component";
import { customStyles_T } from "../../../utils/table_utils";
import Loading from "../loading/Loading";
import type {
  ApiSettlementsTranResponse,
  SettlementTransaction,
} from "../../../interface/interfaces";
import { formatCurrency } from "../../../utils/utils";

const SettlementsTransactionsTable: React.FC<{ ref: string }> = ({ ref }) => {
  const [page, setPage] = useState<number>(1);
  const [perPage, setPerPage] = useState<number>(20);
  const [totalRows, setTotalRows] = useState<number>(0);

  const { data, isLoading, makeRequest } =
    useApiHook<ApiSettlementsTranResponse>(
      `/dashboard/settlement/transactions/${ref}`,
      {
        method: "get",
      }
    );

  useEffect(() => {
    fetchSettlements();
  }, []);

  const fetchSettlements = useCallback(async () => {
    const params = {
      page,
      size: perPage,
    };

    const response = await makeRequest(params);

    // Check for API errors
    if (response?.data?.statusCode !== "00") {
      setTotalRows(0);
    } else if (response?.data?.data) {
      setTotalRows(response.data.data.totalCount || 0);
    }
  }, [page, perPage]);

  // Handle page change
  const handlePageChange = (page: number) => {
    setPage(page);
  };

  const handlePerRowsChange = (newPerPage: number, page: number) => {
    setPerPage(newPerPage);
    setPage(page);
  };

  // Export to CSV function
  const exportToCSV = () => {
    if (!data?.data?.data || data.data.data.length === 0) {
      alert("No data to export");
      return;
    }

    const csvHeaders = [
      "Reference",
      "Amount",
      "Currency",
      "Request ID",
      "Narration",
      "Status",
      "Settlement Date",
    ];

    const csvData = data.data.data.map((row: SettlementTransaction) => [
      row.reference,
      row.amount,
      row.currency,
      row.requestId,
      row.narration,
      row.status,
      row.settlementDate || "N/A",
    ]);

    const csvContent = [
      csvHeaders.join(","),
      ...csvData.map((row) => row.join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `settlement_transactions_${ref}_${
        new Date().toISOString().split("T")[0]
      }.csv`
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const columns: TableColumn<SettlementTransaction>[] = [
    {
      name: "Reference",
      selector: (row) => row.reference,
      sortable: true,
      cell: (row) => (
        <div className="font-medium text-gray-900">{row.reference}</div>
      ),
      width: "200px",
    },
    {
      name: "Amount",
      selector: (row) => row.amount,
      sortable: true,
      cell: (row) => (
        <div className="font-medium">
          {formatCurrency(row.amount, row.currency)}
        </div>
      ),
    },
    {
      name: "Currency",
      selector: (row) => row.currency,
      sortable: true,
      cell: (row) => (
        <div className="font-medium text-gray-700">{row.currency}</div>
      ),
      //   width: "100px",
    },
    {
      name: "Request ID",
      selector: (row) => row.requestId,
      sortable: true,
      cell: (row) => (
        <div className="font-medium text-gray-900">{row.requestId}</div>
      ),
      width: "180px",
    },
    {
      name: "Narration",
      selector: (row) => row.narration,
      sortable: true,
      cell: (row) => <div className="text-gray-700">{row.narration}</div>,
      width: "250px",
    },
    {
      name: "Status",
      selector: (row) => row.status,
      sortable: true,
      cell: (row) => (
        <div
          className={`px-2.5 py-1 rounded-full text-xs font-medium ${
            row.status === "SUCCESSFUL"
              ? "bg-green-100 text-green-800"
              : row.status === "FAILED"
              ? "bg-red-100 text-red-800"
              : "bg-yellow-100 text-yellow-800"
          }`}
        >
          {row.status}
        </div>
      ),
    },
    {
      name: "Settlement Date",
      selector: (row) => row.settlementDate || "",
      sortable: true,
      cell: (row) => (
        <div className="text-gray-700">
          {row.settlementDate
            ? new Date(row.settlementDate).toLocaleDateString()
            : "N/A"}
        </div>
      ),
      //   width: "150px",
    },
  ];

  return (
    <section>
      {/* Export Button */}
      <div className="mb-4 flex justify-end">
        <button
          onClick={exportToCSV}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 transition-colors"
          disabled={!data?.data?.data || data.data.data.length === 0}
        >
          <Download size={16} />
          Export CSV
        </button>
      </div>

      <section>
        <DataTable
          columns={columns}
          data={data?.data?.data || []}
          progressPending={isLoading}
          progressComponent={<Loading />}
          pagination
          paginationServer
          paginationTotalRows={totalRows}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handlePerRowsChange}
          paginationPerPage={perPage}
          paginationRowsPerPageOptions={[10, 20, 30, 50]}
          highlightOnHover
          responsive
          persistTableHead
          customStyles={customStyles_T}
          noDataComponent={
            <div className="p-6 text-center text-gray-500">
              No Settlements found
            </div>
          }
        />
      </section>
    </section>
  );
};

export default SettlementsTransactionsTable;
